import React, { memo, useCallback, useContext, useMemo, MouseEvent } from 'react'
import { HorizontalDotsMinor } from '@shopify/polaris-icons'
import { Payment } from 'src/interfaces/payment'
import { Button } from 'src/components/shared/Button'
import { PaymentStatus, SingleFailureReasonType } from 'src/constants/payment/enums'
import { PaymentsTableContext } from '../../payments-table/PaymentsTable.context'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface Props {
  payment: Payment
}

const PaymentsItemActionButtonComponent = ({ payment }: Props) => {
  const { hasPermission } = useCheckPermissions()

  const { openPaymentsItemAction } = useContext(PaymentsTableContext)

  const handleClickAction = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openPaymentsItemAction(event, payment)
    },
    [payment, openPaymentsItemAction],
  )

  const canOpenActionsMenu = useMemo(() => {
    const canViewInvoice = payment.status === PaymentStatus.Paid && payment.isInvoiceGenerated

    if (canViewInvoice) {
      return true
    }

    const canDeleteSettlement =
      [PaymentStatus.NotPaid, PaymentStatus.RequiresFunding].includes(payment.status) &&
      hasPermission(UserPermission.PaymentsDelete)

    if (canDeleteSettlement) {
      return true
    }

    const canRetrySettlement =
      payment.status === PaymentStatus.Failed &&
      payment.isRetryable &&
      hasPermission(UserPermission.PaymentsProcess)

    if (canRetrySettlement) {
      return true
    }

    const canIgnoreGracePeriod =
      payment.status === PaymentStatus.Failed &&
      payment.failureReasonType === SingleFailureReasonType.ContractorDeactivated &&
      hasPermission(UserPermission.PaymentsIgnoreGracePeriod)

    if (canIgnoreGracePeriod) {
      return true
    }

    const canCancelSettlement =
      Boolean(payment.isCancelable) && hasPermission(UserPermission.PaymentsEdit)

    if (canCancelSettlement) {
      return true
    }

    const canVoidSettlement =
      payment.status === PaymentStatus.Failed && hasPermission(UserPermission.PaymentsEdit)

    if (canVoidSettlement) {
      return true
    }

    return false
  }, [payment, hasPermission])

  return (
    <Button
      data-testid={`payments-main-row-${payment.id}-actions-btn`}
      color="white"
      size="tiny"
      disabled={!canOpenActionsMenu}
      leftIcon={<HorizontalDotsMinor width={20} height={20} />}
      onClick={handleClickAction}
    />
  )
}

export const PaymentsItemActionButton = memo(PaymentsItemActionButtonComponent)
